// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: something the assistant brought up on its own — the brief before a
// meeting, the morning brief, an important mail, an overdue task or a promise
// coming due. The full card reads like the meeting block; a brief that carries
// to-dos (the morning brief) is drawn as a checklist; a nudge is one quiet row
// with its action at the end. Acting on any of them tells the engine the item
// was useful before the action runs as usual.

import styles from '@app/features/konvo_assistant/components/AssistantBriefCard.module.css';
import {AssistantBriefChecklist} from '@app/features/konvo_assistant/components/AssistantBriefChecklist';
import richStyles from '@app/features/konvo_assistant/components/AssistantRichReply.module.css';
import {KonvoCardHead} from '@app/features/konvo_assistant/components/card/KonvoCardHead';
import {isViewAction, openBriefRef} from '@app/features/konvo_assistant/utils/BriefOpen';
import KonvoConnections from '@app/features/konvo_connections/state/KonvoConnections';
import {SafeMarkdown} from '@app/features/messaging/components/markdown';
import {ClickUpLogo} from '@app/features/ui/components/icons/ClickUpLogo';
import {KonvoLogo} from '@app/features/ui/components/icons/KonvoLogo';
import {OutlookLogo} from '@app/features/ui/components/icons/OutlookLogo';
import {WhatsAppLogo} from '@app/features/ui/components/icons/WhatsAppLogo';
import {
	ArrowsOutIcon,
	CalendarBlankIcon,
	CalendarCheckIcon,
	CheckCircleIcon,
	ClockIcon,
	EnvelopeSimpleIcon,
	SunIcon,
} from '@app/features/ui/icons/Icons';
import type {
	AssistantBriefBlock,
	AssistantBriefKind,
	AssistantBriefRef,
	AssistantResponseAction,
} from '@konvo/schema/src/domains/konvo/AssistantResponse';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';

function KindIcon({kind, size}: {kind: AssistantBriefKind; size: number}) {
	switch (kind) {
		case 'meeting_soon':
			return <CalendarBlankIcon size={size} />;
		case 'morning':
			return <SunIcon size={size} />;
		case 'vip_mail':
			return <EnvelopeSimpleIcon size={size} />;
		case 'task_overdue':
			return <ClockIcon size={size} />;
		case 'commitment_due':
			return <CheckCircleIcon size={size} />;
		case 'meeting_recap':
			return <CalendarCheckIcon size={size} />;
	}
}

/** A brief about one mail, chat or task shows where it lives, like the source cards do. */
function RefMark({refKind, size}: {refKind: AssistantBriefRef['kind']; size: number}) {
	switch (refKind) {
		case 'mail':
			return <OutlookLogo size={size} />;
		case 'whatsapp':
			return <WhatsAppLogo size={size} />;
		case 'chat':
			return <KonvoLogo size={size} />;
		case 'clickup':
			return <ClickUpLogo size={size} />;
	}
}

function BriefMark({block, size}: {block: AssistantBriefBlock; size: number}) {
	return block.ref ? <RefMark refKind={block.ref.kind} size={size} /> : <KindIcon kind={block.kind} size={size} />;
}

function useKindLabel(kind: AssistantBriefKind): string {
	const {t} = useLingui();
	switch (kind) {
		case 'meeting_soon':
			return t`Coming up`;
		case 'morning':
			return t`Morning brief`;
		case 'vip_mail':
			return t`Important mail`;
		case 'task_overdue':
			return t`Overdue task`;
		case 'commitment_due':
			return t`You said you would`;
		case 'meeting_recap':
			return t`Meeting recap`;
	}
}

function BriefActions({
	block,
	actions,
	onAction,
	disabled,
	className,
}: {
	block: AssistantBriefBlock;
	actions: ReadonlyArray<AssistantResponseAction>;
	onAction?: (prompt: string) => void;
	disabled: boolean;
	className?: string;
}) {
	const {t} = useLingui();
	const itemId = block.item_id;
	if (!onAction || actions.length === 0) return null;
	return (
		<div className={clsx(richStyles.actions, className)}>
			{actions.map((action) => {
				const opensPreview = Boolean(block.ref && isViewAction(action));
				const previewLabel = opensPreview ? t`Open the preview` : undefined;
				return (
					<button
						key={`${action.label}:${action.prompt}`}
						type="button"
						className={
							opensPreview
								? clsx(richStyles.secondaryAction, styles.previewAction)
								: action.style === 'primary'
									? richStyles.primaryAction
									: richStyles.secondaryAction
						}
						aria-label={previewLabel}
						title={previewLabel}
						disabled={disabled}
						onClick={(event) => {
							KonvoConnections.reportProactiveOutcome(itemId, 'used');
							const ref = block.ref;
							// "Show me the mail": open the mail itself; the assistant is only asked when it cannot be shown here.
							if (ref && isViewAction(action)) {
								const card = event.currentTarget.closest<HTMLElement>('section');
								openBriefRef(ref, block.title, card, () => onAction(action.prompt));
								return;
							}
							onAction(action.prompt);
						}}
						data-flx="konvo-assistant.structured-response.brief.action"
					>
						{opensPreview ? <ArrowsOutIcon size={15} aria-hidden="true" /> : action.label}
					</button>
				);
			})}
		</div>
	);
}

export function AssistantBriefCard({
	block,
	onAction,
	disabled,
}: {
	block: AssistantBriefBlock;
	onAction?: (prompt: string) => void;
	disabled: boolean;
}) {
	const label = useKindLabel(block.kind);
	if (block.style === 'nudge') {
		return (
			<section
				className={styles.nudge}
				data-kind={block.kind}
				data-flx="konvo-assistant.structured-response.nudge"
				aria-label={label}
			>
				<span className={clsx(styles.nudgeIcon, block.ref && styles.nudgeIconSource)} title={label} aria-hidden="true">
					<BriefMark block={block} size={block.ref ? 18 : 15} />
				</span>
				<div className={styles.nudgeText}>
					<strong className={styles.nudgeTitle}>{block.title}</strong>
					{block.body && (
						<span className={styles.nudgeBody}>
							<SafeMarkdown content={block.body} />
						</span>
					)}
				</div>
				{block.when && <span className={styles.nudgeWhen}>{block.when}</span>}
				<BriefActions
					block={block}
					actions={block.actions ?? []}
					onAction={onAction}
					disabled={disabled}
					className={styles.nudgeActions}
				/>
			</section>
		);
	}
	if (block.todos?.length) {
		return (
			<AssistantBriefChecklist
				block={block}
				kindIcon={<KindIcon kind={block.kind} size={16} />}
				label={label}
				onAction={onAction}
				disabled={disabled}
			/>
		);
	}
	return (
		<section
			className={richStyles.suggestion}
			data-kind={block.kind}
			data-flx="konvo-assistant.structured-response.brief"
		>
			<KonvoCardHead
				className={richStyles.suggestionHeadShared}
				icon={<BriefMark block={block} size={16} />}
				label={label}
			/>
			<strong className={richStyles.suggestionTitle}>{block.title}</strong>
			{block.when && (
				<div className={richStyles.suggestionMeta}>
					<span className={richStyles.duration}>
						<ClockIcon size={13} />
						{block.when}
					</span>
				</div>
			)}
			{block.body && (
				<div className={styles.body}>
					<SafeMarkdown content={block.body} />
				</div>
			)}
			<BriefActions block={block} actions={block.actions ?? []} onAction={onAction} disabled={disabled} />
		</section>
	);
}
